<template>
  <div id="app" :class="[lang, isAuthorized && 'has-background']" :style="{backgroundColor: backgroundColor}" >
    <ContentLoading v-if="!isFetchedMainData" type="app" />
    <MaintenanceInProgress v-else-if="isMaintenance" />
    <AgencyLogin v-else-if="isNeedToLogin" />
    <LandingPage v-else-if="isLandingPage && landingInfo" />
    <ContentLoading v-else-if="!isFetchedAllData && !isProcessingBooking" type="app" />
    <Container v-else />

    <CreditFloatingWnd v-if="isShowCredit" :credit-amount="creditAmount" />
    <ProcessBookingModal
      :current-page="currentPage"
      :lang="lang"
      :is-processing-booking="isProcessingBooking"
    />
    <NotifyPopup :open="showNotice" @close="showNotice = false" :content="popupContent" v-if="showNotifyPopup"/>

    <CookieConsentBanner/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { loadProgressBar } from 'axios-progress-bar';
import 'axios-progress-bar/dist/nprogress.css';

export default {
  name: 'App',
  components: {
    Container: () => import('@/components/Container'),
    MaintenanceInProgress: () => import('@/components/atoms/MaintenanceInProgress'),
    ContentLoading: () => import('@/components/atoms/ContentLoading'),
    LandingPage: () => import('@/components/landing/LandingPage'),
    AgencyLogin: () => import('@/components/agencyLogin/login'),
    ProcessBookingModal: () => import('@/components/atoms/ProcessBookingModal'),
    CreditFloatingWnd: () => import('@/components/atoms/CreditFloatingWnd'),
    NotifyPopup: () => import('@/components/NotifyPopup'),
    CookieConsentBanner: () => import('@/components/cookieConsent/CookieConsentBanner'),
  },
  metaInfo() {
    let title = '';
    const metaInfo = [];
    let slugInfo = null;
    const { name, meta, params: { slug } } = this.$route;
    const { destination, chain, dealType } = this.urlParams;
    switch (name) {
      case 'home':
        if (destination !== undefined) {
          title = `${this.baseName} מלונות ב${this.destinationName}`;
          metaInfo.push({ name: 'description', content: `מבצעים ודילים של ${this.baseName} מלונות ${this.destinationName}` });
        } else if (chain !== undefined) {
          title = `${this.baseName} מבצעים ודילים לרשת ${chain}`;
          metaInfo.push({ name: 'description', content: `${this.baseName}, מחירים מיוחדים ובלעדיים למלונות ברשת ${chain}` });
        } else if (dealType !== undefined) {
          title = 'מלונות בארץ';
        } else {
          title = `${this.baseName} מלונות בארץ`;
          metaInfo.push({ name: 'description', content: 'נופש באתר לבודדים במחירי ועדים, מלונות באילת, ים המלח, טבריה, תל אביב, ירושלים ועוד במחירים מצויינים ושרות מעולה' });
        }
        break;
      case 'product-page':
        title = `${this.hotelName} | השטיח המעופף מלון`;
        metaInfo.push({ name: 'description', content: `${this.baseName}, מחירי ועדים להזמנות בודדים במלון, ${this.hotelName} ${this.cityName}מבצעים ל ${this.hotelName} ${this.cityName}` });
        break;
      case 'booking':
        title = `${this.hotelName} ${this.cityName}תשלום | ${this.baseName} | מלון`;
        metaInfo.push({ name: 'description', content: `${this.hotelName} ${this.cityName}עמוד תשלום, חברת ${this.baseName}, מלון` });
        break;
      case 'search-result':
        if (this.privateMessage !== '') this.showAdminMessageForAgent(this.privateMessage, 'private');
        title = `השטיח המעופף תוצאות חיפוש ל${this.cityName}`;
        metaInfo.push({ name: 'description', content: `${this.cityName}הדילים הכי שווים עכשיו בשטיח המעופף. תוצאות חיפוש למלונות ב` });
        break;
      case 'booking-hotel':
        title = `${this.hotelName} השטיח המעופף הזמנת מלון`;
        metaInfo.push({ name: 'description', content: `${this.baseName} - חברת התיירות הגדולה בישראל. מבצעי נופש, דילים, כנסים, אירועים הכל תחת קורת גג אחת` });
        break;
      case 'hotels-page':
        title = `${this.cityName}${this.baseName} | מלונות ב`;
        metaInfo.push({ name: 'description', content: `באתר של ${this.baseName}. מחירי ועדים להזמנות בודדים. מחירים ${this.cityName}כל מלונות מיוחדים למלונות ${this.cityName} אצלנו באתר. ${this.baseName}, חברת התיירות הגדולה בישראל` });
        break;
      case 'verifying-card': case 'card-verified':
        title = `${this.baseName} תשלום מאובטח`;
        metaInfo.push({ name: 'description', content: `${this.baseName} - חברת התיירות הגדולה בישראל. מבצעי נופש, דילים, כנסים, אירועים הכל תחת קורת גג אחת` });
        break;
      case 'slug-info':
        if (this.footerPagesList) {
          // eslint-disable-next-line no-unused-vars
          Object.entries(this.footerPagesList).forEach(([key, slugItem]) => {
            if (slugItem.slug === slug) slugInfo = slugItem;
          });

          if (slugInfo) {
            ({ title } = slugInfo.meta.title);
            metaInfo.push({ name: 'description', content: slugInfo.meta.description });
            metaInfo.push({ name: 'keywords', content: slugInfo.meta.keyword });
          }
        }
        break;
      default:
        title = meta.title ? `${this.baseName} - ${meta.title}` : this.baseName;
    }
    if (this.marketerId !== '') {
      title = this.agencyMarketerContent?.metaTitle ?? title;
      // this.setBackgroundColor(this.agencyMarketerContent?.backgroundColorCss ?? 'white');
    } else if (this.whiteLabel && !this.whiteLabel.forMainSite) {
      title = this.whiteLabel?.metaTitle ?? title;
      // this.setBackgroundColor(this.whiteLabel?.backgroundColorCss ?? 'white');
    }

    if (!this.isRegularSite) {
      metaInfo.push({ name: 'description', content: this.metaDescriptionMarketer });
    }

    document.title = title;
    const origin = window.location.href;
    document.querySelector('head meta[property="og:url"]').setAttribute('content', origin);

    window.prerenderReady = true;
    return {
      title,
      meta: metaInfo,
    };
  },
  data() {
    return {
      destinationImageList: [],
      isMaintenance: false,
      isFetchedAllData: false,              // flag all data is fetched
      isFetchedMainData: false,             // flag main data is fetched ( agency contents is fetched )
      isResourceLoaded: false,              // flag resource is loaded ( hotel chain, category, destination )
      marketerId: '',
      creditRefreshInterval: null,          // Interval ID for refreshing credit amount
      showNotice: true,
      popupContent: '',
      loadStartupPopup: true,
    };
  },
  computed: {
    ...mapGetters({
      lang: 'GET_LANGUAGE',
      currentPage: 'GET_CURRENT_PAGE',
      metaInfo: 'GET_META_INFO',
      whiteLabel: 'GET_WHITE_LABEL_DATA',
      moreHotelInfo: 'GET_ONE_HOTEL_INFO',
      destinationList: 'GET_DOMESTIC_DESTINATION',
      isLanding: 'GET_IS_LANDING_PAGE',
      isLandingChain: 'GET_IS_LANDING_PAGE_CHAIN',
      landingInfo: 'GET_LANDING_INFO',
      footerPagesList: 'GET_FOOTER_PAGES_LIST',
      isAuthorized: 'IS_AGENCY_AUTHORIZED',
      isAgencyLoginState: 'GET_AGENCY_LOGIN_STATE',
      isLoginMarketer: 'GET_IS_LOGIN_MARKETER',
      isOdysseySite: 'GET_ODYSSEY_AGENT_STATE',
      privateMessage: 'GET_PRIVATE_MESSAGE',
      sharedMessage: 'GET_SHARED_MESSAGE',
      errorBody: 'GET_ERROR_RESPONSE',
      isProcessingBooking: 'GET_PROCESSING_BOOKING_STATE',
      isEmalonMode: 'GET_IS_EMALON_MODE',
      isFCLAgent: 'IS_FCL_ODYSSEY_AGENT_CODE',
      agencyMarketerContent: 'GET_MARKETER_AGENCY_CONTENT',
      usePaxBankCreditAsLoginCredential: 'GET_USE_PAX_BANK_CREDIT_AS_LOGIN_CREDENTIAL',
      creditAmount: 'GET_CREDIT_AMOUNT_USD',
    }),
    urlParams() {
      let match;
      const pl = /\+/g,
        search = /([^&=]+)=?([^&]*)/g,
        decode = (s) => decodeURIComponent(s.replace(pl, ' ')),
        query = window.location.search.substring(1);
      const urlParams = {};
      // eslint-disable-next-line no-cond-assign
      while ((match = search.exec(query))) urlParams[decode(match[1])] = decode(match[2]);

      return urlParams;
    },
    isNeedToLogin() {
      return (this.isOdysseySite || this.isLoginMarketer) && !this.isAuthorized;
    },
    isActive() {
      return this.whiteLabel?.active;
    },
    isLandingPage() {
      return this.isLanding || this.isLandingChain;
    },
    isShowCredit() {
      return this.usePaxBankCreditAsLoginCredential && this.isFetchedAllData;
    },
    hotelName() {
      const { moreHotelInfo } = this;
      if (!moreHotelInfo) return '';
      return moreHotelInfo?.hotelConversion?.hotelName || '';
    },
    cityName() {
      const { moreHotelInfo, destinationList } = this;
      if (!moreHotelInfo && !destinationList) return '';
      const mainCityCode = moreHotelInfo?.hotelConversion?.mainCityCode || this.$route.query.city || '';
      return (mainCityCode !== '') ? destinationList.find((item) => (item.code === mainCityCode))?.name : '';
    },
    destinationName() {
      const { destinationList } = this;
      const { destination } = this.urlParams;
      if (!destinationList) return '';
      const dest = destinationList.find((item) => (item.code === destination));
      return dest ? dest.name : '';
    },
    baseName() {
      return this.isEmalonMode ? 'אימלון' : 'השטיח המעופף';
    },
    backgroundColor() {
      let color = 'unset';
      if (this.marketerId !== '') {
        color = this.agencyMarketerContent?.backgroundColorCss ?? 'unset';
      }
      return color;
    },
    isRegularSite() {
      return this.marketerId === '' && this.whiteLabel !== null;
    },
    metaDescriptionMarketer() {
      let description = '';
      if (this.marketerId) {
        description = this.agencyMarketerContent?.metaDescription ?? '';
      } else {
        description = this.whiteLabel?.metaDescription ?? '';
      }
      return description;
    },
    showNotifyPopup() {
      return this.$route.name === 'home' && this.popupContent !== '';
    },
  },
  watch: {
    isAgencyLoginState() {
      // this flag is set when 401 error occurs
      if (this.isAuthorized) {
        window.location.reload();
      }
    },
    async isAuthorized(newVal) {
      if (newVal) {
        // load resource
        this.loadResource();
        this.isFetchedAllData = true;

        // for agent site, initilaize socket connection
        if( this.isOdysseySite ) {
          await this.$store.dispatch('VERIFY_AGENCY_LOGIN_STATE');

          setTimeout(() => {
            this.$store.dispatch('SOCKET_SHARED_CONNECT', this);
            this.$store.dispatch('SOCKET_PRIVATE_CONNECT', this);
          }, 1000);
        } else {
          this.$store.dispatch('SOCKET_DIS_CONNECT', this);
        }
      }
    },
    privateMessage() {
      this.showAdminMessageForAgent(this.privateMessage, 'private');
    },
    sharedMessage() {
      this.showAdminMessageForAgent(this.sharedMessage, 'shared');
    },
    errorBody() {
      if (this.errorBody && this.errorBody !== null) {
        this.showAdminMessageForAgent(`${this.errorBody?.statusText ?? ''} </br> ${this.$t('home.error-message-text')}`, `Error Code: ${this.errorBody?.status ?? ''}'`);
      }
    },
    isProcessingBooking() {
      if (this.isProcessingBooking) {
        this.$bvModal.show('processingBooking');
      } else {
        this.$bvModal.hide('processingBooking');
      }
    },
  },
  methods: {
    resize() {
      let device = 'desktop';
      const { offsetWidth } = document.getElementById('app');
      if (offsetWidth < 479) {
        device = 'mobile';
      } else {
        device = 'desktop';
      }
      this.$store.dispatch('UPDATE_DEVICE', device);

      // set media size(Theme2 use this)
      let mediaSize = 'large-size';
      if (offsetWidth > 867) {
        mediaSize = 'large-size';
      } else {
        mediaSize = 'small-size';
      }
      this.$store.dispatch('UPDATE_MEDIA_SIZE', mediaSize);
    },
    initializeGtag(host) {
      // set google tag analytics
      if (window.location.href.indexOf('google') > -1) {
        this.$store.commit('SET_URL_CONTAIN_GOOGLE');
      } else if (window.location.href.indexOf('facebook') > -1) {
        this.$store.commit('SET_URL_CONTAIN_FACEBOOK');
      }

      // set google tag manager
      window.gtag('js', new Date());
      const debugMode = window.location.href.indexOf('ga-debug=true') > -1 || host.includes('vercel') || host.includes('localhost');
      window.gtag('config', 'G-SGCF8SQQPY', { debug_mode: debugMode });
    },
    injectGTag(host) {
      const marketerId = this.urlParams.marketerId;

      if (!host.includes('agent') && (marketerId === undefined || marketerId === '')) {
        const gTagId = this.isEmalonMode ? 'GTM-K8XF8KL' : 'GTM-MLX2DQM';
        const gtagLayerScript = document.createElement('script');
        gtagLayerScript.setAttribute('id', 'google-tag-manager');
        gtagLayerScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gTagId}');`;
        document.head.appendChild(gtagLayerScript);
      }
    },
    async processUrlParams() {
      const { dealType, subMarketerId, partnerId, marketerId, lang } = this.urlParams;

      // check if landing page, then store landing contents
      if (window.location.pathname === '/') {
        await this.$store.dispatch('STORE_IS_LANDING_STATE', this.urlParams);
      }

      // set dealType
      // if (dealType) {await this.$store.dispatch('FETCH_MARKETER_ID', dealType);}

      // marketerId
      if (marketerId && marketerId !== '') this.$store.commit('SET_MARKETER_ID', marketerId);
      if (partnerId && partnerId !== '') this.$store.commit('SET_PARTNER_ID', partnerId);

      // set MarketerAgency
      // const marketerAgency = dealType;
      // if (marketerAgency) this.$store.commit('SET_AGENCY_FROM_MARKETER_ID', marketerAgency);

      // set SubAgency
      if (subMarketerId && subMarketerId !== '') this.$store.commit('SET_SUB_AGENCY', subMarketerId);
      // if (marketerId) await this.$store.dispatch('FETCH_AGENCY_FROM_MARKETER_ID');
      if (marketerId || dealType) await this.$store.dispatch('FETCH_AGENCY_CONTENT_FROM_MARKETER_ID');
      if (partnerId && partnerId !== '') await this.$store.dispatch('FETCH_PARTNER_CONTENT_FROM_PARTNER_ID');

      // set language
      window.localStorage.setItem('language', lang || 'he');
      await this.$store.dispatch('UPDATE_LANGUAGE', lang || 'he');
    },
    showAdminMessageForAgent(messageBody, type) {
      const title = `${type === 'private' ? this.$t('common-message.private') : this.$t('common-message.share')}`;
      const h = this.$createElement;
      const messageContent = h('div', {
        domProps: {
          innerHTML: messageBody,
        },
      });
      const id = `${type}-${this.count += 1}`;
      const closeButton = h(
        'b-button',
        {
          props: { variant: 'primary', size: 'sm' },
          on: { click: () => this.$bvToast.hide(id) },
        },
        this.$t('common-message.close'),
      );
      this.$bvToast.toast([messageContent, closeButton], {
        id,
        title,
        variant: 'danger',
        solid: true,
        noAutoHide: true,
        toaster: 'b-toaster-top-center',
        bodyClass: ['text-right', 'message-content'],
        noCloseButton: true,
      });
    },
    async loadResource() {
      if(this.isResourceLoaded) return;
      this.isResourceLoaded = true;

      // fetch hotel chain, category, destination
      await this.$store.dispatch('FETCH_HOTEL_CHAIN');
      await this.$store.dispatch('FETCH_HOTEL_CATEGORY');
      await this.$store.dispatch('FETCH_DOMESTIC_DESTINATION');
      await this.$store.dispatch('FETCH_STATIC_PAGE_SUBTYPE_LIST');
      await this.$store.dispatch('FETCH_FOOTER_PAGES_LIST');
      await this.$store.dispatch('STORE_LANDING_URL', this.urlParams);
      await this.$store.dispatch('FETCH_LIVESHOW_PRODUCT_DATA');

      // fetch marketer data for login marketer
      this.refreshCreditAmount();

      // fetch popup notify contents
      if (this.loadStartupPopup) {
        const responseData = await this.$store.dispatch('FETCH_STRING_CONTENTS', { itemId: 'startup', pageId: this.isEmalonMode ? 'emalonHome' :'domesticHome' });
        if (responseData?.caption_Loc?.content) {
          this.popupContent = responseData.caption_Loc.content;
        }
      }
    },
    processAfterLoading() {
      if (this.isFCLAgent) {
        const onsiteScript = document.createElement('script');
        onsiteScript.setAttribute('id', 'onsite');
        onsiteScript.innerHTML = `(function(d, t) {
              var g = d.createElement(t),
              s = d.getElementsByTagName(t)[0];
              g.src = "https://cdn.inwebr.com/inwebr_dcfc4e2dd12c91ef939d6dc745c5a952.js";
              s.parentNode.insertBefore(g, s);
          }(document, "script"));`;
        document.head.appendChild(onsiteScript);
      }
    },
    refreshCreditAmount() {
      if(this.usePaxBankCreditAsLoginCredential) {
        this.$store.dispatch('FETCH_MARKETER_CREDIT_AMOUNT', { currency: 'IS' });
      }
    },
    startCreditRefresh() {
      this.creditRefreshInterval = setInterval(() => {
        this.refreshCreditAmount();
      }, 10 * 1000); // 10 seconds interval
    },
    stopCreditRefresh() {
      if (this.creditRefreshInterval) {
        clearInterval(this.creditRefreshInterval);
        this.creditRefreshInterval = null;
      }
    },
  },
  async created() {
    let { host } = window.location;
    this.marketerId = this.urlParams.marketerId;

    this.initializeGtag(host);

    // -------------  configure development params ----------------------- //
    if (host.includes('domestic-dev.vercel.app')) this.$store.commit('SET_STAGING_STATE', true);
    if (host.includes('domestic-front.vercel.app')) this.$store.commit('SET_STAGING_STATE', true);
    if (host.includes('localhost')) this.$store.commit('SET_STAGING_STATE', true);
    if (host.includes('flyingcarpet.co.il')) this.$store.commit('SET_STAGING_STATE', false);

    if (host.includes('localhost') || host.includes('vercel')) host = 'www.flyingcarpet.co.il';
    // if (host.includes('localhost') || host.includes('vercel')) host = 'agent.flyingcarpet.co.il'; // agent site
    // if (host.includes('localhost') || host.includes('vercel')) host = 'www.emalon.co.il'; // for Emalon site
    // ------------------------------------------------------------------- //

    // inject gtag scripts
    this.injectGTag(host);

    // get agency contents (!!! FETCH MAIN DATA !!!)
    const response = await this.$store.dispatch('FETCH_WHITE_LABEL', host);
    if ( response.data?.status === 404) {
      this.isFetchedMainData = true;
      this.isMaintenance = true;
      return;
    }

    // do process according the url params
    await this.processUrlParams();

    // Verify agency session if the site is Odyssey or the user was logged in before
    if (this.isOdysseySite || window.sessionStorage.getItem('agencyLogin') === 'true') {
      await this.$store.dispatch('VERIFY_AGENCY_LOGIN_STATE');
    }

    this.isFetchedMainData = true;

    // If the site is Odyssey and the user is not logged in as an agency agent, stop further execution
    if ((this.isOdysseySite || this.isLoginMarketer) && !this.isAuthorized) {
      return;
    }

    // fetch endpoints for loading resource
    await this.loadResource();
    this.isFetchedAllData = true;

    // process after loading
    this.processAfterLoading();
  },
  mounted() {
    this.$store.dispatch('cookieConsentInit');

    // set event handler
    window.addEventListener('resize', this.resize);

    window.addEventListener('beforeunload', () => {
      this.$store.dispatch('SOCKET_DIS_CONNECT', this);
    });

    // set scroll behavior
    this.resize();
    window.scrollTo(0, 0);
    loadProgressBar({ showSpinner: false });
    // this.$bvModal.show('processingBooking');

    // setTimeout(() => {
      // this.isShowCredit = false; // Hide the welcome window after 3 seconds
    // }, 3000);

    if(this.usePaxBankCreditAsLoginCredential) {
      this.startCreditRefresh(); // Start refreshing credit amount
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.resize);
    this.stopCreditRefresh(); // Stop refreshing credit amount
  },
};
</script>

<style lang="sass">
@import '~bootstrap/scss/bootstrap.scss'
@import '~bootstrap-vue/src/index.scss'
.agile
  &__nav-button
    background: #fff
    border: 1px solid #4285f4
    color: #4285f4
    cursor: pointer
    font-size: 20px
    height: 40px
    position: absolute
    top: calc(50% - 40px)
    transition-duration: .3s
    width: 40px
    border-radius: 50%
    transition: 0.5s all

    &:hover
      border: none
      color: black
      box-shadow: gray -1px 1px 10px
      opacity: 1

    &--prev
      left: 0

    &--next
      right: 0

  &__dots
    bottom: 10px
    left: 50%
    position: absolute
    transform: translateX(-50%)

  &__dot
    margin: 0 10px

    button
      background-color: transparent
      border: 1px solid #fff
      border-radius: 50%
      cursor: pointer
      display: block
      height: 10px
      font-size: 0
      line-height: 0
      margin: 0
      padding: 0
      transition-duration: .3s
      width: 10px

    &--current,
    &:hover
      button
        background-color: #fff
</style>

<style>
#app {
  font-family: "ploni", "Poppins", sans-serif;
  text-align: right;
  height: 100%;
}
#app.has-background {
  background-image: url('/assets/img/banner_landing.jpg');
  background-repeat: no-repeat;
  background-size: cover;
}
html {
  scroll-behavior: smooth;
}
 #nprogress .bar {
  background: rgb(0, 255, 149) !important;
  height: 5px;
}
#nprogress .peg {
  box-shadow: 0 0 10px rgb(0, 255, 149), 0 0 5px rgb(0, 255, 149) !important;
}
#nprogress .spinner-icon {
  border-top-color: rgb(0, 255, 149) !important;
  border-left-color: rgb(0, 255, 149) !important;
}
.st-content{
  scroll-behavior: smooth;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
}
</style>
